'use client';

import { c as _c } from "react/compiler-runtime";
import type { EmblaCarouselType } from 'embla-carousel';
import { useState } from 'react';
import { Button } from '@/app/components/button';
import { ButtonGroup } from '@/app/components/button-group';
import { HeaderLevel2 } from '@/app/components/headers/header-level-2';
import { Container } from '@/app/components/layouts/container';
import { Section } from '@/app/components/layouts/section';
import { Sliders } from '@/app/components/sliders';
import { usePaginationSlider } from '@/app/components/sliders/hooks/usePaginationSlider';
import styles from '@/app/cv/components/sections/products/index.module.css';
import { SlideContent } from '@/app/cv/components/sections/products/intro/slide-content';
import { SlideImage } from '@/app/cv/components/sections/products/intro/slide-image';
import { CV_PRODUCT_EVENT_NAME } from '@/app/cv/components/sections/products/tracking';
import type { ProductIntroSectionProps } from '@/app/cv/components/sections/products/types';
import { useRevealAnim } from '@/app/hooks/useRevealAnim';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';
export const ProductIntro = t0 => {
  const $ = _c(55);
  const {
    theme: t1,
    className,
    lead,
    title,
    buttons: t2,
    items: t3,
    testId: t4
  } = t0;
  const theme = t1 === undefined ? "light" : t1;
  let t5;
  if ($[0] !== t2) {
    t5 = t2 === undefined ? [] : t2;
    $[0] = t2;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const buttons = t5;
  let t6;
  if ($[2] !== t3) {
    t6 = t3 === undefined ? [] : t3;
    $[2] = t3;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const items = t6;
  const testId = t4 === undefined ? "product-intro" : t4;
  let t7;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      defaultBaseDelay: 0.04
    };
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  useRevealAnim(t7);
  const [emblaApi, setEmblaApi] = useState(null);
  const {
    selectedIndex
  } = usePaginationSlider(emblaApi);
  const isDark = theme === "dark";
  const itemCount = items.length;
  const t8 = !isDark;
  let t9;
  if ($[5] !== className || $[6] !== isDark || $[7] !== t8) {
    t9 = cn({
      "bg-linear-to-t from-gray-800 to-gray-600": isDark,
      "bg-white": t8
    }, className);
    $[5] = className;
    $[6] = isDark;
    $[7] = t8;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const t10 = `${testId}-section`;
  let t11;
  if ($[9] !== t10) {
    t11 = createDataAttributes({
      test: t10
    });
    $[9] = t10;
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  let t12;
  if ($[11] !== isDark) {
    t12 = cn("mb-6 sm:mb-10", {
      "text-white": isDark
    });
    $[11] = isDark;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  const t13 = `${testId}-container`;
  let t14;
  if ($[13] !== t13) {
    t14 = createDataAttributes({
      test: t13
    });
    $[13] = t13;
    $[14] = t14;
  } else {
    t14 = $[14];
  }
  const t15 = title || "";
  const t16 = isDark ? "text-white" : undefined;
  let t17;
  if ($[15] !== buttons || $[16] !== isDark) {
    let t18;
    if ($[18] !== isDark) {
      t18 = btn => ({
        ...btn,
        color: isDark ? "white" : "black",
        className: cn("hidden lg:inline-flex", btn.className)
      });
      $[18] = isDark;
      $[19] = t18;
    } else {
      t18 = $[19];
    }
    t17 = buttons.map(t18);
    $[15] = buttons;
    $[16] = isDark;
    $[17] = t17;
  } else {
    t17 = $[17];
  }
  let t18;
  if ($[20] !== lead || $[21] !== t15 || $[22] !== t16 || $[23] !== t17 || $[24] !== theme) {
    t18 = <HeaderLevel2 lead={lead} title={t15} theme={theme} leadClassName={t16} content2ClassName="space-y-2" buttons={t17} />;
    $[20] = lead;
    $[21] = t15;
    $[22] = t16;
    $[23] = t17;
    $[24] = theme;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  let t19;
  if ($[26] !== t12 || $[27] !== t14 || $[28] !== t18) {
    t19 = <Container className={t12} data-revealanim={true} {...t14}>{t18}</Container>;
    $[26] = t12;
    $[27] = t14;
    $[28] = t18;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  const t20 = `${testId}-content`;
  let t21;
  if ($[30] !== t20) {
    t21 = createDataAttributes({
      test: t20
    });
    $[30] = t20;
    $[31] = t21;
  } else {
    t21 = $[31];
  }
  let t22;
  if ($[32] !== isDark || $[33] !== itemCount || $[34] !== items[0]) {
    t22 = itemCount === 1 && <div className="grid gap-6 lg:grid-cols-2 lg:items-end lg:gap-10 xl:gap-16" data-revealanim={true} {...createDataAttributes({
      test: "slide-0"
    })}>{!!items[0]?.image && <SlideImage {...items[0]} />}<SlideContent {...items[0]} isDark={isDark} /></div>;
    $[32] = isDark;
    $[33] = itemCount;
    $[34] = items[0];
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  let t23;
  if ($[36] !== isDark || $[37] !== itemCount || $[38] !== items || $[39] !== selectedIndex) {
    t23 = itemCount >= 2 && <Sliders onEmblaInit={setEmblaApi} align="center" hidden={false} containerClassName="-ml-4 sm:-ml-6 lg:ml-0 lg:gap-x-10" emblaOptions={{
      breakpoints: {
        ...(itemCount >= 3 ? {
          "(min-width: 1280px)": {
            active: false
          }
        } : {
          "(min-width: 1024px)": {
            active: false
          }
        })
      }
    }}>{items.map((item, index) => <div key={index} className={cn("grid shrink-0 content-start gap-6", styles.carouselSlide, styles.introSlide, {
        [styles.introSlideThree]: itemCount >= 3
      })} {...createDataAttributes({
        test: `slide-${index}`,
        active: index === selectedIndex ? true : undefined
      })}>{!!item.image && <SlideImage {...item} />}<SlideContent {...item} isDark={isDark} /></div>)}</Sliders>;
    $[36] = isDark;
    $[37] = itemCount;
    $[38] = items;
    $[39] = selectedIndex;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[41] !== t21 || $[42] !== t22 || $[43] !== t23) {
    t24 = <Container {...t21}>{t22}{t23}</Container>;
    $[41] = t21;
    $[42] = t22;
    $[43] = t23;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] !== buttons || $[46] !== isDark || $[47] !== testId) {
    t25 = !!buttons.length && <Container className="mt-6 sm:mt-10 lg:hidden" data-revealanim={true}><ButtonGroup {...createDataAttributes({
        test: `${testId}-button-group`
      })}>{buttons.map((btn_0, index_0) => <Button key={index_0} {...btn_0} className={cn("w-full md:w-auto", btn_0.className)} color={isDark ? "white" : "black"} {...createDataAttributes({
          test: `button-${index_0}`
        })} {...createDataGA({
          eventName: CV_PRODUCT_EVENT_NAME,
          group: testId,
          click: true,
          text: btn_0.children,
          url: btn_0.href
        })} />)}</ButtonGroup></Container>;
    $[45] = buttons;
    $[46] = isDark;
    $[47] = testId;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  let t26;
  if ($[49] !== t11 || $[50] !== t19 || $[51] !== t24 || $[52] !== t25 || $[53] !== t9) {
    t26 = <Section size="lg" className={t9} data-revealanim-group={true} {...t11}>{t19}{t24}{t25}</Section>;
    $[49] = t11;
    $[50] = t19;
    $[51] = t24;
    $[52] = t25;
    $[53] = t9;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  return t26;
};